// Profile page — settings, cloud-sync status, local-only toggle, data export,
// account deletion request.

import { resetDisclaimer as resetCycleDisclaimer } from '../lib/cycle_store';
import { supabase, whoAmI, isLocalOnly, setLocalOnly } from '../lib/supabase';
import { migrateLocalToCloud } from '../lib/stack_store';
import { isTier1Enabled, setTier1, isTier2Enabled, setTier2 } from '../lib/analytics';
import { modalConfirm, modalAlert } from '../lib/modal';

export async function renderProfile() {
  const app = document.getElementById('app')!;
  const user = await whoAmI();
  const localOnly = isLocalOnly();

  app.innerHTML = `
    <div class="detail-header">
      <h1>Profile</h1>
      <p class="sci">Account, sync, and your data.</p>
    </div>

    <section class="detail-section">
      <h3>Account</h3>
      ${user ? `
        <p>Signed in as <strong>${esc(user.email || '(no email)')}</strong></p>
        <p style="color:var(--dim);font-size:12px;">id: ${esc(user.id.slice(0, 8))}…</p>
        <button id="sign-out" class="btn">Sign out</button>
      ` : `
        <p>You're not signed in. Your data lives only in this browser.</p>
        <a href="/login" class="btn btn-primary">Sign in</a>
      `}
    </section>

    <section class="detail-section">
      <h3>Sync</h3>
      <p style="color:var(--dim);font-size:13px;">
        ${user && !localOnly
          ? 'Your stacks and tracking data sync to your account so you can pick up on any device you sign into.'
          : (user && localOnly
            ? 'Local-only mode is ON. Your data stays in this browser, even though you\'re signed in.'
            : 'Sign in to sync across devices.')}
      </p>
      <label style="display:flex;align-items:center;gap:10px;padding:10px;background:var(--bg-3);border-radius:6px;cursor:pointer;margin-top:10px;">
        <input type="checkbox" id="local-only" ${localOnly ? 'checked' : ''} />
        <span>Local-only mode <span style="color:var(--dim);font-size:12px;">— don't sync my data to my account</span></span>
      </label>
      ${user && !localOnly ? `
        <button id="upload-local" class="btn" style="margin-top:10px;">Upload my local data to my account</button>
        <p style="font-size:12px;color:var(--dim);margin-top:4px;">Use this once if you used MyStack offline before signing in and want your local stacks pushed to your account.</p>
      ` : ''}
    </section>

    <section class="detail-section">
      <h3>Help improve MyStack</h3>
      <label style="display:flex;align-items:flex-start;gap:10px;padding:10px;background:var(--bg-3);border-radius:6px;cursor:pointer;margin-bottom:10px;">
        <input type="checkbox" id="tier1" ${isTier1Enabled() ? 'checked' : ''} />
        <span>
          <strong>Anonymous usage data</strong><br/>
          <span style="color:var(--dim);font-size:12px;">Tap-level analytics so we know which features and compounds matter. No identity attached. Default on; toggle anytime.</span>
        </span>
      </label>
      <label style="display:flex;align-items:flex-start;gap:10px;padding:10px;background:var(--bg-3);border-radius:6px;cursor:pointer;">
        <input type="checkbox" id="tier2" ${isTier2Enabled() ? 'checked' : ''} />
        <span>
          <strong>Unlock cohort insights — anonymized research data</strong><br/>
          <span style="color:var(--dim);font-size:12px;">Shares your stacks, doses, and biometric trends — stripped of your name, email, and account id. In return you get access to cohort-driven features (real-world dose data, "what's working" correlations, stack templates from top performers) as they roll out. Toggle off any time.</span>
        </span>
      </label>
    </section>

    <section class="detail-section">
      <h3>Warnings</h3>
      <p style="color:var(--dim);font-size:13px;">Reset every dismissed disclaimer or banner — the cycle-tab one and any future ones.</p>
      <button id="reset-warnings" class="btn">Reset all warnings</button>
    </section>

    <section class="detail-section">
      <h3>Export your data</h3>
      <p style="color:var(--dim);font-size:13px;">Download everything stored in this browser as JSON.</p>
      <button id="export-json" class="btn btn-primary">Export JSON</button>
    </section>

    <section class="detail-section">
      <h3>Wipe local data</h3>
      <p style="color:var(--dim);font-size:13px;">Removes stacks, cycles, tracking history, and fasting log from THIS browser only. Cloud account data is untouched.</p>
      <button id="wipe-local" class="btn" style="color:#E25C5C;border-color:#E25C5C;">Wipe local data</button>
    </section>

    ${user ? `
      <section class="detail-section">
        <h3>Delete account & cloud data</h3>
        <p style="color:var(--dim);font-size:13px;line-height:1.5;">
          This deletes every row in your account: stacks, items, tracking entries, cycles, fasting sessions, and your profile.
          Your auth login is flagged for permanent removal — an admin batch job honors flagged requests within a few days.
          You can sign back in within that window to cancel. After the job runs, the account and all data are gone.
        </p>
        <button id="delete-account" class="btn" style="color:#E25C5C;border-color:#E25C5C;">Request account deletion</button>
      </section>
    ` : ''}

    <section class="detail-section">
      <h3>About</h3>
      <p>MyStack v0.1. Supplement & nutrition tracker. Free. Citation-first. Not medical advice.</p>
      <p style="color:var(--dim);font-size:12px;line-height:1.5;">This website does not sell ANY products, or endorse any product or group of products based on any information presented herein. All informational product links are pointing to pre-existing public listings of legal products offered by independent sellers on the amazon.com platform. All cited information discussing any product or compound seen on this website is paraphrased from studies cited as sources for all paraphrased information.</p>
      <p style="color:var(--dim);font-size:12px;">Your tracking data stays yours and isn't sold.</p>
    </section>
  `;

  document.getElementById('tier1')?.addEventListener('change', (e) => { setTier1((e.target as HTMLInputElement).checked); });
  document.getElementById('tier2')?.addEventListener('change', (e) => { setTier2((e.target as HTMLInputElement).checked); });

  document.getElementById('sign-out')?.addEventListener('click', async () => {
    await supabase.auth.signOut();
    location.assign('/');
  });

  document.getElementById('local-only')?.addEventListener('change', (e) => {
    setLocalOnly((e.target as HTMLInputElement).checked);
    renderProfile();
  });

  document.getElementById('upload-local')?.addEventListener('click', async () => {
    const ok = await modalConfirm({
      title: 'Upload local stacks?',
      body: 'Pushes every stack stored in this browser up to your account.',
      confirmLabel: 'Upload',
    });
    if (!ok) return;
    const n = await migrateLocalToCloud();
    await modalAlert({
      title: 'Upload complete',
      body: `Uploaded ${n} stack${n === 1 ? '' : 's'} to your account.`,
    });
  });

  document.getElementById('reset-warnings')?.addEventListener('click', async () => {
    const ok = await modalConfirm({
      title: 'Reset every dismissed warning?',
      body: 'All banners and disclaimers will show again on next visit.',
      confirmLabel: 'Reset',
    });
    if (!ok) return;
    resetCycleDisclaimer();
    localStorage.removeItem('mystack:web:disclaimer:v1');
    await modalAlert({
      title: 'Warnings reset',
      body: 'They will show again on next visit.',
    });
  });

  document.getElementById('export-json')?.addEventListener('click', () => {
    const dump: Record<string, unknown> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('mystack:')) {
        try { dump[k] = JSON.parse(localStorage.getItem(k) || 'null'); }
        catch { dump[k] = localStorage.getItem(k); }
      }
    }
    const blob = new Blob([JSON.stringify(dump, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `mystack-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  });

  document.getElementById('wipe-local')?.addEventListener('click', async () => {
    const ok = await modalConfirm({
      title: 'Wipe ALL local MyStack data?',
      body: 'Removes stacks, cycles, tracking history, and fasting log from this browser only. Cloud account data is untouched. Cannot be undone.',
      confirmLabel: 'Wipe local data',
      danger: true,
    });
    if (!ok) return;
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('mystack:')) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
    await modalAlert({ title: 'Local data wiped', body: 'This browser is reset.' });
  });

  document.getElementById('delete-account')?.addEventListener('click', async () => {
    if (!user) return;
    const ok = await modalConfirm({
      title: 'Delete your account and ALL cloud data?',
      body: 'Your rows are removed immediately. Your sign-in is flagged for full removal by an admin job within a few days. Sign back in during that window to cancel.',
      confirmLabel: 'Delete account',
      danger: true,
    });
    if (!ok) return;
    // Best-effort row purge (RLS will only allow the user's own rows).
    await supabase.from('mystack_stack_items').delete().neq('id', '00000000-0000-0000-0000-000000000000');
    await supabase.from('mystack_stacks').delete().eq('owner_id', user.id);
    await supabase.from('mystack_tracking_entries').delete().eq('owner_id', user.id);
    await supabase.from('mystack_cycle_starts').delete().eq('owner_id', user.id);
    await supabase.from('mystack_custom_cycles').delete().eq('owner_id', user.id);
    await supabase.from('mystack_fasting_sessions').delete().eq('owner_id', user.id);
    await supabase.from('mystack_users').update({ deletion_requested_at: new Date().toISOString() }).eq('id', user.id);
    await modalAlert({
      title: 'Cloud data deleted',
      body: 'Your account is flagged for full removal — sign back in within a few days to cancel.',
    });
    await supabase.auth.signOut();
    location.assign('/');
  });
}

function esc(s: string) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
